<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-building"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">Company Wise Report</div>
          <div class="panel-header__sub">Fingerprint service transactions grouped by company</div>
        </div>
        <div class="panel-header__action" v-if="reports.length > 0 && !getLoadingData && search.company !== 'all'">
          <button class="btn-invoice" @click="createInvoice">
            <i class="fa fa-file-invoice"></i> Generate Invoice
          </button>
        </div>
      </div>

      <!-- Sticky filter toolbar -->
      <div class="filter-bar">
        <div class="filter-bar__row">
          <adv-search
            @search="advSearch"
            :is_company="true"
            :is_date_range="true"
            :is_service="true"
            :is_status="true"
            :is_payment_status="true"
            :reportData="excelreports"
            :titleworksheet="titleworksheet"
            :json_fields="json_fields"
            :header="header"
            :report_name="'company_report.xls'"
          />
        </div>
      </div>

      <!-- Loading -->
      <div class="state-box" v-if="getLoadingData">
        <div class="state-spinner"></div>
        <span>Loading report data…</span>
      </div>

      <!-- Prompt -->
      <div class="state-box state-box--muted" v-else-if="load">
        <i class="fa fa-filter state-box__icon"></i>
        <div class="state-box__title">Select search criteria</div>
        <div class="state-box__sub">Use the filters above to generate a report</div>
      </div>

      <!-- Empty -->
      <div class="state-box state-box--muted" v-else-if="!load && reports.length === 0">
        <i class="fa fa-inbox state-box__icon"></i>
        <div class="state-box__title">No records found</div>
        <div class="state-box__sub">Try adjusting your filters</div>
      </div>

      <!-- Table -->
      <div class="table-wrap" v-if="reports.length > 0 && !getLoadingData">
        <table class="report-table">
          <thead>
            <tr>
              <th class="th-check">
                <label class="checkbox-wrap">
                  <input type="checkbox" v-model="selectAll" @change="selectAllApp" />
                  <span class="checkbox-box"></span>
                </label>
              </th>
              <th class="th-sm">#</th>
              <th class="th-sm">App No.</th>
              <th v-if="search.company === 'all'">Company</th>
              <th>Applicant</th>
              <th>Service</th>
              <th>Location</th>
              <th>Service Status</th>
              <th>Payment</th>
              <th>Invoice No.</th>
              <th>Date</th>
              <th class="th-amount">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in reports" :key="index" :class="{ 'row--selected': item.checked }">
              <td class="td-check">
                <label class="checkbox-wrap">
                  <input type="checkbox" v-model="item.checked" @change="check_app" />
                  <span class="checkbox-box"></span>
                </label>
              </td>
              <td class="td-sm">{{ index + 1 }}</td>
              <td class="td-sm td-mono">{{ item.id }}</td>
              <td v-if="search.company === 'all'" class="td-company">{{ item.company_name }}</td>
              <td>
                <div class="applicant-name">{{ item.applicant }}</div>
                <div class="applicant-tcn" v-if="item.tcn_number">({{ item.tcn_number }})</div>
              </td>
              <td>{{ item.service }}</td>
              <td class="td-location">{{ item.client }}</td>
              <td>
                <span class="status-chip" :class="serviceStatusClass(item.service_status)">
                  {{ item.service_status }}
                </span>
              </td>
              <td>
                <span class="status-chip" :class="paymentStatusClass(item.payment_status)">
                  {{ item.payment_status }}
                </span>
              </td>
              <td class="td-mono">{{ item.company_invoice_no || '—' }}</td>
              <td class="td-date">
                <template v-if="item.enroll_date">{{ dateFormat(item.enroll_date) }}</template>
                <template v-else-if="item.schedule_date == null">N/A</template>
                <template v-else>{{ dateFormat(item.schedule_date) }}</template>
              </td>
              <td class="td-amount">
                $ {{ (item.amount || 0) | toFixedTwoDigit }}
              </td>
            </tr>

            <!-- Grand total row -->
            <tr class="grand-total-row">
              <td :colspan="search.company === 'all' ? 11 : 10" class="grand-total-label">
                Grand Total
              </td>
              <td class="td-amount grand-total-val">
                $ {{ grand_total | toFixedTwoDigit }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>
</template>

<script>
import moment from "moment";
import {
  FETCH_CLIENTS,
  FETCH_SERVICES,
  FETCH_COMPANY_WISE_REPORT,
  FETCH_MASTER_COMPANIES
} from "@/core/services/store/actions.type";
import {
  SET_BREADCRUMB,
  SET_COMPANY_INVOICE_APPOINTMENTS
} from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import AdvSearch from "@/includes/search/AdvSearch";

export default {
  components: { AdvSearch },
  computed: {
    ...mapGetters(["currentUser", "getClients", "getServices", "getMasterCompanies", "getSearchDate"]),
    grand_total() {
      return this.reports.reduce((sum, e) => sum + (e.amount || 0), 0);
    }
  },
  filters: {
    toFixedTwoDigit(amount) {
      return parseFloat(amount).toFixed(2);
    }
  },
  data() {
    return {
      selectAll: false,
      show_invoice: false,
      search: {
        service: "all",
        company: "all",
        date_from: "",
        date_to: "",
        service_status: "all",
        payment_status: "all"
      },
      titleworksheet: "",
      json_fields: {
        Company: "company_name",
        "Applicant Name": "applicant",
        Service: "service",
        Location: "client",
        "Service Status": "service_status",
        "Payment Status": "payment_status",
        "Scheduled Date": "schedule_date",
        Amount: "amount"
      },
      getLoadingData: false,
      reports: [],
      header: [],
      load: true,
      excelreports: [],
      breadCrumbs: [
        { id: 1, title: "Dashboard",                              route: "client.dashboard" },
        { id: 2, title: "Report",                                 route: "" },
        { id: 3, title: "Company Wise Fingerprint Service Report", route: "" }
      ]
    };
  },
  mounted() {
    this.$store.dispatch(FETCH_CLIENTS);
    this.$store.commit(SET_COMPANY_INVOICE_APPOINTMENTS, []);
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.$store.dispatch(FETCH_SERVICES, this.currentUser.u.is_client ? "self" : "all");
    this.$store.dispatch(FETCH_MASTER_COMPANIES);
  },
  methods: {
    dateFormat(date) {
      return date ? moment(date).format("MM/DD/YYYY") : "N/A";
    },
    serviceStatusClass(status) {
      if (!status) return "chip--grey";
      const s = status.toLowerCase();
      if (s === "completed" || s === "c") return "chip--green";
      if (s === "pending"   || s === "p") return "chip--orange";
      if (s === "cancel"    || s === "canceled") return "chip--red";
      return "chip--grey";
    },
    paymentStatusClass(status) {
      if (!status) return "chip--grey";
      const s = status.toLowerCase();
      if (s === "paid"   || s === "p") return "chip--green";
      if (s === "unpaid" || s === "u") return "chip--orange";
      return "chip--grey";
    },
    advSearch(value) {
      this.selectAll = false;
      this.getLoadingData = true;
      this.load = false;
      const { header, search, footer } = value;
      this.header = header;
      this.titleworksheet = search.date_from + " - " + search.date_to;
      this.search = { ...search };
      this.tablereset();
      this.$store
        .dispatch(FETCH_COMPANY_WISE_REPORT, search)
        .then(res => {
          this.getLoadingData = false;
          this.reports = res.data.map(data => ({
            id:                   data.id,
            abbreviation_code:    data.abbreviation_code,
            checked:              false,
            company_name:         data.company_name,
            schedule_date:        data.schedule_date,
            enroll_date:          data.enroll_date,
            service:              data.service,
            client:               data.client,
            applicant:            data.applicant,
            fullname:             data.fullname,
            service_status:       data.service_status,
            payment_status:       data.payment_status,
            company_invoice_no:   data.company_invoice_no,
            amount:               data.amount,
            client_display_name:  data.client_display_name,
            company_invoiced:     data.company_invoiced,
            tcn_number:           data.tcn_number
          }));
          this.excelreports = [...res.data];
          const total = res.data.reduce((s, e) => s + e.amount, 0);
          if (res.data.length > 0) {
            this.excelreports.push({ company_name: "", schedule_date: "Grand Total", service: "", client: "", applicant: "", service_status: "", payment_status: "", amount: total });
          }
          this.excelreports.push({ company_name: "", schedule_date: footer[0], service: "", client: "", applicant: "", service_status: "", payment_status: "", amount: footer[1] });
        })
        .catch(err => {
          this.getLoadingData = false;
          this.msg = err.error;
        });
    },
    tablereset() {
      if (this.search.company !== "all") {
        this.json_fields = { "Applicant Name": "applicant", Service: "service", Location: "client", "Service Status": "service_status", "Payment Status": "payment_status", "Scheduled Date": "schedule_date", Amount: "amount" };
      } else {
        this.json_fields = { Company: "company_name", "Applicant Name": "applicant", Service: "service", Location: "client", "Service Status": "service_status", "Payment Status": "payment_status", "Scheduled Date": "schedule_date", Amount: "amount" };
      }
    },
    selectAllApp() {
      this.show_invoice = this.selectAll;
      this.reports.forEach(app => { app.checked = this.selectAll; });
    },
    check_app() {
      const any = this.reports.some(r => r.checked);
      this.show_invoice = any;
      if (!any) this.selectAll = false;
    },
    createInvoice() {
      this.$store.commit(SET_COMPANY_INVOICE_APPOINTMENTS, []);
      if (this.search.company === "all") {
        this.$toastr.e("Please select a company");
        return;
      }
      const ids = this.reports.filter(r => r.checked).map(r => r.id);
      if (ids.length === 0) {
        this.$toastr.e("Please select at least one appointment to generate the invoice");
        return;
      }
      window.localStorage.setItem("company_invoice_appointment", ids);
      this.$store.commit(SET_COMPANY_INVOICE_APPOINTMENTS, ids);
      this.$router.push({
        name: "company.report.invoicedetail",
        params: { slug: this.$route.params.client_slug, company_id: this.search.company }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$green:  #276749;
$border: #dde2ec;

@keyframes spin { to { transform: rotate(360deg); } }

.page-wrap {
  padding: 12px;
  font-family: $font;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ── header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px; height: 48px; border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 18px; flex-shrink: 0;
  }

  &__text { flex: 1; }
  &__title { font-size: 16px; font-weight: 700; color: $dark; line-height: 1.2; }
  &__sub   { font-size: 12px; color: #7a8499; margin-top: 3px; }
  &__action { flex-shrink: 0; }
}

.btn-invoice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: $green;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;
  &:hover { background: #1d5230; box-shadow: 0 4px 12px rgba(39,103,73,0.3); }
}

/* ── filter bar ── */
.filter-bar {
  border-bottom: 1px solid $border;
  background: #fafbfd;
  position: sticky;
  top: 0;
  z-index: 20;

  &__row { padding: 8px 16px 12px; }
}

/* ── state boxes ── */
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  gap: 8px;
  font-family: $font;
  font-size: 13px;
  color: #7a8499;

  &--muted .state-box__icon { color: #c5d0e0; }
  &__icon  { font-size: 36px; margin-bottom: 4px; }
  &__title { font-size: 15px; font-weight: 600; color: $dark; }
  &__sub   { font-size: 13px; color: #7a8499; }
}

.state-spinner {
  width: 32px; height: 32px;
  border: 3px solid #c5d8f5;
  border-top-color: $blue;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-bottom: 8px;
}

/* ── table ── */
.table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 560px;
  padding: 0 24px 24px;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: $dark;

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 10px 12px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #7a8499;
    border-bottom: 2px solid $border;
    white-space: nowrap;
    background: #fafbfd;
  }

  .th-check  { width: 36px; }
  .th-sm     { width: 56px; text-align: center; }
  .th-amount { text-align: right; min-width: 120px; white-space: nowrap; }

  td {
    padding: 10px 12px;
    border-bottom: 1px solid #f0f2f8;
    vertical-align: middle;
  }

  tr:hover td { background: #f8fbff; }
  tr:last-child td { border-bottom: none; }

  .row--selected td { background: #f0f7ff; }

  .td-check    { text-align: center; }
  .td-sm       { text-align: center; color: #a0aab8; font-size: 12px; }
  .td-mono     { font-family: 'Courier New', monospace; font-size: 12px; color: #5a6278; }
  .td-company  { font-weight: 500; }
  .td-location { color: #5a6278; }
  .td-date     { white-space: nowrap; font-size: 12px; color: #5a6278; }
  .td-amount   { text-align: right; font-variant-numeric: tabular-nums; font-weight: 500; white-space: nowrap; min-width: 120px; }
}

/* ── applicant cell ── */
.applicant-name { font-size: 13px; }
.applicant-tcn  { font-size: 11px; color: #a0aab8; margin-top: 1px; }

/* ── status chips ── */
.status-chip {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.03em;
  background: #f0f3fa;
  color: #7a8499;

  &.chip--green  { background: rgba(39,103,73,0.1);  color: $green; }
  &.chip--orange { background: rgba(217,119,6,0.1);  color: #92520a; }
  &.chip--red    { background: rgba(180,35,24,0.1);  color: #b42318; }
  &.chip--grey   { background: #f0f3fa; color: #7a8499; }
}

/* ── grand total row ── */
.grand-total-row td { background: #f4f6fb !important; border-top: 2px solid $border; border-bottom: 2px solid $border; }

.grand-total-label {
  text-align: right;
  font-size: 12px;
  font-weight: 700;
  color: $dark;
  padding-right: 16px !important;
}

.grand-total-val {
  font-size: 13px;
  font-weight: 700;
  color: $dark;
  font-variant-numeric: tabular-nums;
}

/* ── custom checkbox ── */
.checkbox-wrap {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  margin: 0;

  input[type="checkbox"] { display: none; }

  .checkbox-box {
    width: 16px;
    height: 16px;
    border: 1.5px solid $border;
    border-radius: 4px;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, border-color 0.15s;
    flex-shrink: 0;
  }

  input:checked ~ .checkbox-box {
    background: $blue;
    border-color: $blue;

    &::after {
      content: '';
      width: 4px;
      height: 8px;
      border: 2px solid #fff;
      border-top: none;
      border-left: none;
      transform: rotate(45deg) translateY(-1px);
      display: block;
    }
  }
}
</style>
